import { useCallback, useEffect, useState, type FormEvent } from "react";
import { NavLink, useParams } from "react-router-dom";
import { CheckCheck, Inbox, Search, Send } from "lucide-react";
import { request } from "../lib/api";
import { formatDate } from "../lib/datetime";
import {
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "../components/ui";

type Status = "open" | "waiting_for_user" | "resolved";
type Ticket = {
  id: string;
  category: string;
  subject: string;
  description: string;
  status: Status;
  requester: string;
  assigned_id: string;
  requester_role: string;
  created_at: string;
  updated_at: string;
};
type Message = {
  id: string;
  author_role: string;
  author: string;
  body: string;
  created_at: string;
};
type Detail = { ticket: Ticket; messages: Message[] };

const statusLabel: Record<Status, string> = {
  open: "Open",
  waiting_for_user: "Waiting for user",
  resolved: "Resolved",
};

function date(value: string) {
  return formatDate(value);
}

function ticketsPath(filter: "" | Status, search: string, offset?: number) {
  const params = new URLSearchParams();
  if (filter) params.set("status", filter);
  if (search) params.set("q", search);
  if (offset !== undefined) params.set("offset", String(offset));
  const query = params.toString();
  return `/api/admin/support/tickets${query ? `?${query}` : ""}`;
}

export function SupportTickets() {
  const { ticketID } = useParams();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [filter, setFilter] = useState<"" | Status>("");
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState<{ id: string; message: string } | null>(null);
  const [actionError, setActionError] = useState("");
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState("");

  const loadTickets = useCallback(async () => {
    const result = await request<{ tickets: Ticket[]; next_offset: number | null }>(
      ticketsPath(filter, searchTerm)
    );
    setTickets(result.tickets);
    setNextOffset(result.next_offset);
  }, [filter, searchTerm]);
  const loadDetail = useCallback(async () => {
    if (!ticketID) return;
    const result = await request<Detail>(`/api/admin/support/tickets/${ticketID}`);
    setDetail(result);
  }, [ticketID]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      if (next === searchTerm) return;
      setTickets([]);
      setNextOffset(null);
      setListError("");
      setLoading(true);
      setSearchTerm(next);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, searchTerm]);
  useEffect(() => {
    let active = true;
    request<{ tickets: Ticket[]; next_offset: number | null }>(ticketsPath(filter, searchTerm))
      .then((result) => {
        if (active) {
          setTickets(result.tickets);
          setNextOffset(result.next_offset);
        }
      })
      .catch((cause) => {
        if (active)
          setListError(cause instanceof Error ? cause.message : "Could not load requests");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filter, searchTerm]);
  useEffect(() => {
    if (!ticketID) return;
    let active = true;
    request<Detail>(`/api/admin/support/tickets/${ticketID}`)
      .then((result) => {
        if (active) {
          setDetail(result);
          setDetailError(null);
        }
      })
      .catch((cause) => {
        if (active)
          setDetailError({
            id: ticketID,
            message: cause instanceof Error ? cause.message : "Could not open request",
          });
      });
    return () => {
      active = false;
    };
  }, [ticketID]);

  async function loadMore() {
    if (nextOffset === null || moreLoading) return;
    setMoreLoading(true);
    setMoreError("");
    try {
      const result = await request<{ tickets: Ticket[]; next_offset: number | null }>(
        ticketsPath(filter, searchTerm, nextOffset)
      );
      setTickets((current) => [...current, ...result.tickets]);
      setNextOffset(result.next_offset);
    } catch (cause) {
      setMoreError(cause instanceof Error ? cause.message : "Could not load older requests");
    } finally {
      setMoreLoading(false);
    }
  }

  async function sendReply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ticketID || !reply.trim() || saving) return;
    setSaving(true);
    setActionError("");
    try {
      await request(`/api/admin/support/tickets/${ticketID}/messages`, {
        method: "POST",
        body: JSON.stringify({ body: reply.trim() }),
      });
      setReply("");
      try {
        await Promise.all([loadDetail(), loadTickets()]);
      } catch {
        setActionError(
          "Reply saved, but this view could not refresh. Reopen the request to see it."
        );
      }
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not send reply");
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(next: "open" | "resolved") {
    if (!ticketID || saving) return;
    setSaving(true);
    setActionError("");
    try {
      await request(`/api/admin/support/tickets/${ticketID}`, {
        method: "PATCH",
        body: JSON.stringify({ status: next }),
      });
      try {
        await Promise.all([loadDetail(), loadTickets()]);
      } catch {
        setActionError(
          "Status saved, but this view could not refresh. Reopen the request to see it."
        );
      }
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Could not update request");
    } finally {
      setSaving(false);
    }
  }

  const visible = tickets;

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(18rem,0.9fr)_minmax(28rem,1.4fr)]">
      <section className="min-w-0 rounded-xl border border-border bg-surface shadow-sm">
        <div className="border-b border-border p-4">
          <div className="flex flex-wrap gap-2">
            {(["", "open", "waiting_for_user", "resolved"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => {
                  if (filter === value) return;
                  setFilter(value);
                  setTickets([]);
                  setNextOffset(null);
                  setListError("");
                  setLoading(true);
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === value ? "bg-primary text-content-on-primary" : "bg-surface-muted text-content-secondary"}`}
              >
                {value ? statusLabel[value] : "All"}
              </button>
            ))}
          </div>
          <label className="relative mt-4 block">
            <span className="sr-only">Search requests</span>
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-content-secondary"
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title or requester"
              className={`${inputClass} pl-9`}
            />
          </label>
        </div>
        {loading ? (
          <p className="p-6 text-sm text-content-secondary">Loading requests…</p>
        ) : listError ? (
          <p role="alert" className="p-6 text-sm text-danger">
            {listError}
          </p>
        ) : visible.length === 0 ? (
          <p className="p-6 text-sm text-content-secondary">No requests match this view.</p>
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((ticket) => (
              <li key={ticket.id}>
                <NavLink
                  to={`/support/${ticket.id}`}
                  onClick={() => {
                    setActionError("");
                    setReply("");
                  }}
                  className={`block p-4 hover:bg-surface-muted ${ticketID === ticket.id ? "bg-surface-muted" : ""}`}
                >
                  <strong className="block line-clamp-2 text-sm text-content-primary">
                    {ticket.subject}
                  </strong>
                  <span className="mt-1 block text-xs text-content-secondary">
                    {ticket.requester} · {ticket.requester_role} · {date(ticket.updated_at)}
                  </span>
                  <span className="mt-2 inline-block rounded-full bg-surface-muted px-2 py-1 text-xs font-semibold text-content-secondary">
                    {statusLabel[ticket.status]}
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
        {moreError && (
          <p role="alert" className="px-4 py-2 text-sm text-danger">
            {moreError}
          </p>
        )}
        {nextOffset !== null && !loading && (
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={moreLoading}
            className="w-full border-t border-border px-4 py-3 text-sm font-semibold text-interactive disabled:opacity-50"
          >
            {moreLoading ? "Loading…" : "Load older requests"}
          </button>
        )}
      </section>

      <section className="min-w-0 rounded-xl border border-border bg-surface p-5 shadow-sm">
        {!ticketID ? (
          <div className="grid min-h-72 place-content-center justify-items-center gap-3 text-center text-content-secondary">
            <Inbox size={30} />
            <p className="text-sm">Select a request to view the conversation.</p>
          </div>
        ) : detailError?.id === ticketID ? (
          <p role="alert" className="text-sm text-danger">
            {detailError.message}
          </p>
        ) : !detail || detail.ticket.id !== ticketID ? (
          <p className="text-sm text-content-secondary">Opening request…</p>
        ) : (
          <div className="grid gap-5">
            <header>
              <h2 className="text-lg font-semibold text-content-primary">
                {detail.ticket.subject}
              </h2>
              <p className="mt-1 text-sm text-content-secondary">
                {detail.ticket.requester} · {detail.ticket.assigned_id} ·{" "}
                {detail.ticket.requester_role}
              </p>
              <p className="mt-1 text-xs text-content-secondary">
                {detail.ticket.category.replaceAll("_", " ")} · {statusLabel[detail.ticket.status]}{" "}
                · Opened {date(detail.ticket.created_at)}
              </p>
            </header>
            <div className="grid max-h-[36rem] gap-3 overflow-y-auto border-y border-border py-4">
              <article className="rounded-lg bg-surface-muted p-4">
                <p className="mb-2 text-xs font-semibold text-content-secondary">
                  {detail.ticket.requester} · {date(detail.ticket.created_at)}
                </p>
                <p className="whitespace-pre-wrap break-words text-sm leading-6 text-content-primary">
                  {detail.ticket.description}
                </p>
              </article>
              {detail.messages.map((message) => (
                <article
                  key={message.id}
                  className={`rounded-lg p-4 ${message.author_role === "staff" ? "bg-blue-50" : "bg-surface-muted"}`}
                >
                  <p className="mb-2 text-xs font-semibold text-content-secondary">
                    {message.author} · {date(message.created_at)}
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm leading-6 text-content-primary">
                    {message.body}
                  </p>
                </article>
              ))}
            </div>
            <div>
              {detail.ticket.status === "resolved" ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void changeStatus("open")}
                  className={secondaryButtonClass}
                >
                  Reopen request
                </button>
              ) : (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void changeStatus("resolved")}
                  className={secondaryButtonClass}
                >
                  <CheckCheck size={16} /> Mark resolved
                </button>
              )}
            </div>
            <form onSubmit={(event) => void sendReply(event)} className="grid gap-3">
              <label className="grid gap-2 text-sm font-semibold text-content-primary">
                Reply to requester
                <textarea
                  required
                  maxLength={4000}
                  rows={4}
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  placeholder="Write a helpful response…"
                  className={textareaClass}
                />
              </label>
              {actionError && (
                <p role="alert" className="text-sm text-danger">
                  {actionError}
                </p>
              )}
              <button
                type="submit"
                disabled={saving || !reply.trim()}
                className={primaryButtonClass}
              >
                <Send size={16} /> {saving ? "Saving…" : "Send reply"}
              </button>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
